import { BodyWeightApiService } from './api/BodyWeightApiService';

export class BodyWeightService {
  /**
   * Fetch bodyweight data for a specific date
   * @param dateKey - Date in YYYY-MM-DD format
   * @returns Bodyweight data with string values for form compatibility
   */
  static async fetchBodyWeightForDate(dateKey: string): Promise<{
    weight: string;
    fatPercentage: string;
    waistCircumference: string;
  }> {
    const data = await BodyWeightApiService.getBodyWeightForDate(dateKey);

    return {
      weight: data && typeof data.weight !== 'undefined' ? data.weight.toString() : '',
      fatPercentage: data && typeof data.fat_percentage !== 'undefined' && data.fat_percentage !== null
        ? data.fat_percentage.toString()
        : '',
      waistCircumference: data && typeof data.waist_circumference !== 'undefined' && data.waist_circumference !== null
        ? data.waist_circumference.toString()
        : '',
    };
  }

  /**
   * Log bodyweight for a specific date
   * @param dateKey - Date in YYYY-MM-DD format
   * @param data - Bodyweight data from form (string values)
   * @returns Response from the API
   */
  static async logBodyWeight(
    dateKey: string,
    data: {
      weight: string;
      fatPercentage?: string;
      waistCircumference?: string;
    }
  ): Promise<Response> {
    if (!data.weight) {
      throw new Error('Weight is required');
    }

    const requestData: {
      weight: number;
      fat_percentage?: number;
      waist_circumference?: number;
    } = {
      weight: parseFloat(data.weight)
    };

    // Only include fat_percentage if provided and not empty
    if (data.fatPercentage && data.fatPercentage.trim() !== '') {
      requestData.fat_percentage = parseFloat(data.fatPercentage);
    }

    // Only include waist_circumference if provided and not empty
    if (data.waistCircumference && data.waistCircumference.trim() !== '') {
      requestData.waist_circumference = parseFloat(data.waistCircumference);
    }

    return BodyWeightApiService.logBodyWeight(dateKey, requestData);
  }

  /**
   * Fetch the most recent body weight value before a given date
   * @param dateKey - Date in YYYY-MM-DD format
   * @returns Weight as a number, or null if no prior entry
   */
  static async fetchPreviousBodyWeight(dateKey: string): Promise<number | null> {
    const data = await BodyWeightApiService.getPreviousBodyWeight(dateKey);
    return data ? data.weight : null;
  }

  /**
   * Fetch all dates with bodyweight data for a specific month
   * @param year - Year (e.g., 2026)
   * @param month - Month number (1-12)
   * @returns Array of date strings in YYYY-MM-DD format
   */
  static async fetchBodyWeightDates(year: number, month: number): Promise<string[]> {
    return BodyWeightApiService.getBodyWeightDates(year, month);
  }
}
